import { cn, useDateFormatter } from '@nexelem/utils';
import { useCustomTranslation } from '../../../../../hooks';
import { BatchGanttViewBase, TaskGanttView } from '@nexelem/graphne-api';
import { useGroupedQueuesStore, queueByIdSelector } from '../../store';
import { Badge, Status } from '@nexelem/components';

import { CUSTOM_ICONS } from '@nexelem/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserGroup } from '@fortawesome/pro-light-svg-icons';
import { formatRequiredWorkers } from '../../utils/formatRequiredWorkers';
import { useFormatTaskDuration } from '../../Gantt/hooks';

type ProductProps = {
  node: TaskGanttView;
  batchNode?: BatchGanttViewBase;
  className?: string;
};

export const Product = ({ className, node, batchNode }: ProductProps) => {
  const { tWrapper } = useCustomTranslation('products.productionPlan');
  const { tWrapper: tWrapperModal } = useCustomTranslation('products.productionPlan.assignWorkerModal');

  const dateWithHoursFormatter = useDateFormatter({ displayWithHours: true });
  const formatTaskDuration = useFormatTaskDuration({ withoutDuration: true });

  const { dueDate, start, end, queueId } = node;

  const queue = useGroupedQueuesStore(queueByIdSelector(queueId));

  return (
    <div className={cn('rounded-xs border border-border-container bg-surface-container-lighter', className)}>
      <div className="border-b border-border-container px-4 py-3">
        <div className="flex text-s11-sb text-text-secondary-darker">
          {dueDate && (
            <span className="uppercase">
              {tWrapper('dueDate')} {dateWithHoursFormatter(dueDate)}
            </span>
          )}
          <span className="ml-auto uppercase">
            {tWrapper('production')} {formatTaskDuration({ startDate: new Date(start), endDate: new Date(end) })}
          </span>
        </div>
        <div className="mt-1 flex justify-between">
          {batchNode && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-s16">Batch {batchNode.name}</span>
              <Badge StartIcon={<CUSTOM_ICONS.Merge />}>{batchNode.tasks?.length || 0}</Badge>
            </div>
          )}
          <span className="ml-auto flex items-center gap-2">
            <span className="ml-4 text-s16-sb">{queue?.name}</span>
            <span className="h-4 min-h-4 w-4 min-w-4 rounded-full" style={{ backgroundColor: `#${queue?.area.hex}` }} />
          </span>
        </div>
      </div>

      {[...(batchNode?.tasks || [node])].map((batchTask) => (
        <div className="border-t border-border-container px-4 py-3" key={batchTask.id}>
          <div className="text-s11-sb text-text-secondary-darker uppercase">
            {tWrapper('productId')} {batchTask.name}
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-s16-sb">{batchTask.productName}</span>
            <span className="text-s16">
              {batchTask.progress.totalAmount.amount} {batchTask.progress.totalAmount.unit.name}
            </span>
          </div>
        </div>
      ))}

      <div className="flex items-center gap-2 border-t border-border-container px-4 py-3">
        <Status
          color="orange"
          Icon={<FontAwesomeIcon className="ml-2" icon={faUserGroup} />}
          showDot={false}
          variant="contained"
          text={
            formatRequiredWorkers(node.requiredSoftResourcesAmount) === null
              ? tWrapperModal('workerCountPartial')
              : tWrapperModal('workerCount', { count: formatRequiredWorkers(node.requiredSoftResourcesAmount) || 0 })
          }
        />
        {/* <Badge>mock FB2C.9</Badge>
        <Badge>mock FB1A.1</Badge>
        <IconButton Icon={ProLightCircleQuestion} /> */}
      </div>
    </div>
  );
};
